import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Package, Wrench, XCircle, Archive, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { profile } = useAuth();
  const [stats, setStats] = useState({ inStock: 0, mounted: 0, inRepair: 0, scrapped: 0 });
  const [scrapByMonth, setScrapByMonth] = useState([]);
  const [damageTypes, setDamageTypes] = useState([]);
  const [avgLifetime, setAvgLifetime] = useState(0);
  const [anomalies, setAnomalies] = useState([]);
  const [lowStock, setLowStock] = useState(false);

  useEffect(() => {
    if (profile) fetchDashboardData();
  }, [profile]);

  const fetchDashboardData = async () => {
    try {
      const { data: tires, error: tiresError } = await supabase
        .from('tires').select('status').eq('company_id', profile.company_id);
      if (tiresError) throw tiresError;

      const statsData = {
        inStock: tires.filter(t => t.status === 'stock').length,
        mounted: tires.filter(t => t.status === 'mounted').length,
        inRepair: tires.filter(t => t.status === 'repair').length,
        scrapped: tires.filter(t => t.status === 'scrap').length,
      };
      setStats(statsData);
      setLowStock(statsData.inStock < 5);

      const { data: scraps, error: scrapsError } = await supabase
        .from('scrap_records').select('*, tires!inner(company_id)').eq('tires.company_id', profile.company_id);
      if (scrapsError) throw scrapsError;

      const monthlyScrap = {};
      scraps.forEach(scrap => {
        const month = new Date(scrap.scrapped_at).toLocaleDateString('id-ID', { month: 'short', year: '2-digit' });
        monthlyScrap[month] = (monthlyScrap[month] || 0) + 1;
      });
      setScrapByMonth(Object.entries(monthlyScrap).map(([month, count]) => ({ month, count })));

      const damageCount = {};
      scraps.forEach(scrap => {
        if (scrap.damage_types && Array.isArray(scrap.damage_types)) {
          scrap.damage_types.forEach(type => { damageCount[type] = (damageCount[type] || 0) + 1; });
        }
      });
      setDamageTypes(Object.entries(damageCount).map(([name, value]) => ({ name, value })));

      const totalKm = scraps.reduce((sum, s) => sum + (s.km_total || 0), 0);
      setAvgLifetime(scraps.length > 0 ? Math.round(totalKm / scraps.length) : 0);

      const { data: flags } = await supabase
        .from('anomaly_flags').select('*, trucks(truck_number)')
        .eq('company_id', profile.company_id).order('flagged_at', { ascending: false }).limit(5);
      setAnomalies(flags || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Gagal memuat data dasbor');
    }
  };

  const COLORS = ['#dc2626', '#ea580c', '#ca8a04', '#65a30d', '#0891b2'];

  return (
    <div className="p-4 sm:p-8" data-testid="dashboard">
      <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-widest text-zinc-900 mb-8" style={{ fontFamily: 'Chivo' }}>
        DASBOR
      </h1>

      {lowStock && (
        <div className="bg-red-600 border border-red-500 p-4 mb-6 flex items-center gap-3" data-testid="low-stock-alert">
          <Warning size={24} weight="bold" className="text-white" />
          <span className="text-white font-mono uppercase text-sm tracking-wider">PERINGATAN STOK RENDAH: Hanya {stats.inStock} ban tersedia</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-zinc-200 p-6 shadow-sm" data-testid="stat-in-stock">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Di Stok</p>
            <Package size={24} weight="bold" className="text-zinc-400" />
          </div>
          <p className="text-2xl font-mono text-zinc-900 tracking-tight">{stats.inStock}</p>
        </div>

        <div className="bg-white border border-zinc-200 p-6 shadow-sm" data-testid="stat-mounted">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Terpasang</p>
            <Archive size={24} weight="bold" className="text-zinc-400" />
          </div>
          <p className="text-2xl font-mono text-zinc-900 tracking-tight">{stats.mounted}</p>
        </div>

        <div className="bg-white border border-zinc-200 p-6 shadow-sm" data-testid="stat-in-repair">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Diperbaiki</p>
            <Wrench size={24} weight="bold" className="text-amber-500" />
          </div>
          <p className="text-2xl font-mono text-zinc-900 tracking-tight">{stats.inRepair}</p>
        </div>

        <div className="bg-white border border-zinc-200 p-6 shadow-sm" data-testid="stat-scrapped">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Rusak</p>
            <XCircle size={24} weight="bold" className="text-red-600" />
          </div>
          <p className="text-2xl font-mono text-zinc-900 tracking-tight">{stats.scrapped}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-zinc-200 p-6 shadow-sm" data-testid="monthly-scrap-chart">
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-2 mb-6" style={{ fontFamily: 'Chivo' }}>
            BAN RUSAK BULANAN
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={scrapByMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="month" stroke="#71717a" style={{ fontSize: '12px', fontFamily: 'IBM Plex Mono' }} />
              <YAxis stroke="#71717a" style={{ fontSize: '12px', fontFamily: 'IBM Plex Mono' }} />
              <Tooltip
                contentStyle={{ background: '#ffffff', border: '1px solid #d4d4d8', borderRadius: '0' }}
                labelStyle={{ color: '#18181b', fontFamily: 'IBM Plex Mono' }}
              />
              <Bar dataKey="count" fill="#dc2626" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-zinc-200 p-6 shadow-sm" data-testid="damage-type-chart">
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-2 mb-6" style={{ fontFamily: 'Chivo' }}>
            JENIS KERUSAKAN
          </h2>
          {damageTypes.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={damageTypes} cx="50%" cy="50%" labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80} fill="#8884d8" dataKey="value"
                >
                  {damageTypes.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#ffffff', border: '1px solid #d4d4d8', borderRadius: '0' }}
                  labelStyle={{ color: '#18181b', fontFamily: 'IBM Plex Mono' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-zinc-500 font-mono text-sm text-center py-20">Tidak ada data ban rusak</p>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-6 shadow-sm mb-8" data-testid="avg-lifetime">
        <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-2 mb-4" style={{ fontFamily: 'Chivo' }}>
          RATA-RATA UMUR BAN
        </h2>
        <p className="text-4xl font-mono text-zinc-900 tracking-tight">{avgLifetime.toLocaleString('id-ID')} KM</p>
      </div>

      <div className="bg-white border border-zinc-200 p-6 shadow-sm" data-testid="anomaly-log">
        <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-2 mb-4" style={{ fontFamily: 'Chivo' }}>
          LOG ANOMALI
        </h2>
        {anomalies.length > 0 ? (
          <div className="space-y-3">
            {anomalies.map((anomaly) => (
              <div key={anomaly.id} className="bg-red-50 border border-red-600 p-4 flex items-start gap-3" data-testid="anomaly-item">
                <Warning size={20} weight="bold" className="text-red-600 mt-1" />
                <div className="flex-1">
                  <p className="text-zinc-900 font-mono text-sm">
                    Truk {anomaly.trucks?.truck_number} - Posisi {anomaly.position_number}
                  </p>
                  <p className="text-zinc-600 font-mono text-xs mt-1">
                    Diharapkan: {anomaly.expected_serial} | Ditemukan: {anomaly.reported_serial}
                  </p>
                  <p className="text-zinc-500 font-mono text-xs mt-1">
                    {new Date(anomaly.flagged_at).toLocaleString('id-ID')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-500 font-mono text-sm">Tidak ada anomali terdeteksi</p>
        )}
      </div>
    </div>
  );
}
